"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import NextLink from "next/link";
import { Box, Typography } from "@mui/material";
import DesktopMenu from "@/src/components/main-site/_partials/header/desktop-menu/desktop-menu";
import DrawerMenu from "@/src/components/journal-site/_partials/header/journal-menu/drawer-menu/drawer-menu";
import MenuItem from "@/src/_types/menu-item";
import { mapToMenuItems } from "@/src/utils/database-helpers/menu-helper";
import {
  actionSx,
  doubleRule,
  FONT_DISPLAY,
  FONT_TEXT,
  FONT_UI,
  hairline,
  SHEET_GUTTER,
  SHEET_MAX_WIDTH,
} from "@/src/components/common/broadsheet/tokens";
import { journalBaseHref, normalizeSitePath } from "@/src/utils/paths";

const PIN_AT = 220;

export default function JournalMasthead({ journal }: { journal: any }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const isJournalHome = normalizeSitePath(usePathname()) === "/";
  const barRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const evaluate = () => setPinned(window.scrollY > PIN_AT);
    evaluate();
    window.addEventListener("scroll", evaluate, { passive: true });
    return () => window.removeEventListener("scroll", evaluate);
  }, []);

  const menuItems: MenuItem[] = journal.menu
    ? mapToMenuItems(journal.menu, journal)
    : [];

  const base = journalBaseHref(journal);
  const home = base ?? "/";
  const guideLabel =
    journal.options?.template_configs?.authors_guide?.guide?.label ??
    "Authors guide";
  const guideHref = base ? `${base}/knowledgebase` : "#";
  const panelHref = journal.slug ? `${journal.slug}/user/login` : "#";
  const submitHref = panelHref;

  const issn = [
    journal.meta_data?.issn_epub
      ? `e-ISSN ${journal.meta_data.issn_epub}`
      : null,
    journal.meta_data?.issn_ppub
      ? `p-ISSN ${journal.meta_data.issn_ppub}`
      : null,
  ].filter(Boolean);

  const actions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.375 }}>
      <Box component="a" href={guideHref} sx={actionSx("outline", "small")}>
        {guideLabel}
      </Box>
      <Box
        component="a"
        href={submitHref}
        target="_blank"
        rel="noopener noreferrer"
        sx={actionSx("solid", "small")}
      >
        Submit manuscript
      </Box>
    </Box>
  );

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: "custom.newsprint",
        color: "custom.ink",
      }}
    >
      <Box
        sx={{
          maxWidth: SHEET_MAX_WIDTH,
          mx: "auto",
          px: SHEET_GUTTER,
          ...doubleRule,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            pt: { xs: 0.375, sm: 0.75 },
            pb: 0.25,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              ml: "auto",
            }}
          >
            {journal.is_main_site ? null : (
              <Box
                component="a"
                href={panelHref}
                target="_blank"
                rel="noopener noreferrer"
                sx={actionSx("outline", "small")}
              >
                Sign in
              </Box>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            columnGap: 1.5,
            rowGap: 0.375,
            pb: 0.625,
            ...hairline,
          }}
        >
          <Box sx={{ display: "flex", minWidth: 0, flex: "1 1 24rem" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component={isJournalHome ? "h1" : "p"}
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2.125rem" },
                  lineHeight: 1.05,
                  letterSpacing: "0.02em",
                  "& a": {
                    color: "custom.ink",
                    textDecoration: "none",
                    "&:hover": { color: "custom.link" },
                  },
                }}
              >
                <NextLink href={home}>{journal.title}</NextLink>
              </Typography>
              {journal.meta_data?.subtitle ? (
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    mt: 0.125,
                    maxWidth: "62ch",
                    fontFamily: FONT_TEXT,
                    fontStyle: "italic",
                    fontSize: { xs: "0.8125rem", md: "0.9375rem" },
                    lineHeight: 1.4,
                    color: "custom.inkMuted",
                  }}
                >
                  {journal.meta_data.subtitle}
                </Typography>
              ) : null}
            </Box>
          </Box>

          {issn.length ? (
            <Box
              sx={{
                flex: "none",
                textAlign: { sm: "right" },
                fontFamily: FONT_UI,
                fontSize: "0.6875rem",
                lineHeight: 1.6,
                color: "custom.subtitleText",
              }}
            >
              {issn.map((line) => (
                <Box key={line}>{line}</Box>
              ))}
            </Box>
          ) : null}
        </Box>

        <Box
          ref={barRef}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.5,
            minHeight: "2.5rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <Box
              component="button"
              type="button"
              aria-label="Open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{
                ...actionSx("outline", "small"),
                display: { xs: "inline-flex", lg: "none" },
                cursor: "pointer",
              }}
            >
              Menu
            </Box>
            <Box
              component="nav"
              aria-label="Journal"
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "stretch",
                minWidth: 0,
              }}
            >
              <DesktopMenu menuItems={menuItems} />
            </Box>
          </Box>
          <Box sx={{ display: { xs: "none", md: "flex" }, flex: "none" }}>
            {actions}
          </Box>
        </Box>
      </Box>

      {pinned ? (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            backgroundColor: "custom.newsprint",
            ...doubleRule,
            boxShadow: "0 6px 18px rgba(0,0,0,.14)",
          }}
        >
          <Box
            sx={{
              maxWidth: SHEET_MAX_WIDTH,
              mx: "auto",
              px: SHEET_GUTTER,
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center",
              columnGap: 0.75,
              minHeight: "2.75rem",
              "& > *": { minWidth: 0 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
              <Box
                component="button"
                type="button"
                aria-label="Open menu"
                onClick={() => setDrawerOpen(true)}
                sx={{
                  ...actionSx("outline", "small"),
                  display: { xs: "inline-flex", lg: "none" },
                  cursor: "pointer",
                }}
              >
                Menu
              </Box>
              <Typography
                component="p"
                sx={{
                  m: 0,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 700,
                  fontSize: "1rem",
                  lineHeight: 1.2,
                  color: "custom.ink",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {journal.title}
              </Typography>
            </Box>
            <Box
              component="nav"
              aria-label="Journal, condensed"
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "stretch",
                justifyContent: "center",
              }}
            >
              <DesktopMenu menuItems={menuItems} />
            </Box>
            <Box
              sx={{
                display: { xs: "none", md: "flex" },
                justifyContent: "flex-end",
              }}
            >
              {actions}
            </Box>
          </Box>
        </Box>
      ) : null}

      <DrawerMenu
        open={drawerOpen}
        toggleDrawer={setDrawerOpen}
        menuItems={menuItems}
        journalTitle={journal.title}
        journalSubtitle={journal.meta_data?.subtitle ?? null}
        authorGuide={{ label: guideLabel, link: guideHref }}
        submission={{ label: "Submit manuscript", link: submitHref }}
      />
    </Box>
  );
}
